'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function GcfCalculator() {
  const t = useTranslations("tools.gcfCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [numbersInput, setNumbersInput] = useState('330, 75, 450, 225');
  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const gcd = (a: number, b: number): number => {
    a = Math.abs(a);
    b = Math.abs(b);
    while (b !== 0) {
      const temp = b;
      b = a % b;
      a = temp;
    }
    return a;
  };

  const gcdMultiple = (numbers: number[]): number => {
    return numbers.reduce((acc, num) => gcd(acc, num));
  };

  const primeFactorization = (n: number): number[] => {
    const factors: number[] = [];
    let num = Math.abs(n);

    while (num % 2 === 0) {
      factors.push(2);
      num = num / 2;
    }

    for (let i = 3; i <= Math.sqrt(num); i += 2) {
      while (num % i === 0) {
        factors.push(i);
        num = num / i;
      }
    }

    if (num > 2) {
      factors.push(num);
    }

    return factors;
  };

  const findCommonFactors = (factorizations: number[][]): number[] => {
    if (factorizations.length === 0) return [];

    const firstFactors = [...factorizations[0]];
    const common: number[] = [];

    for (const factor of firstFactors) {
      if (factorizations.every(factors => factors.includes(factor))) {
        common.push(factor);
        factorizations = factorizations.map(factors => {
          const index = factors.indexOf(factor);
          return [...factors.slice(0, index), ...factors.slice(index + 1)];
        });
      }
    }

    return common;
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults(null);

    const input = numbersInput.trim();
    const numberStrings = input.split(',').map(s => s.trim()).filter(s => s);

    if (numberStrings.length < 2) {
      setError(t('errorMinNumbers'));
      return;
    }

    const numbers = numberStrings.map(s => parseInt(s, 10));

    if (numbers.some(n => isNaN(n) || n <= 0)) {
      setError(t('errorPositiveIntegers'));
      return;
    }

    if (numbers.some(n => n > 1000000)) {
      setError(t('errorTooLarge'));
      return;
    }

    const gcfValue = gcdMultiple(numbers);
    const factorizations = numbers.map(n => primeFactorization(n));
    const commonFactors = findCommonFactors(factorizations.map(f => [...f]));

    const euclideanSteps: any[] = [];
    if (numbers.length === 2) {
      let a = Math.max(...numbers);
      let b = Math.min(...numbers);
      let stepNum = 1;

      while (b !== 0) {
        const quotient = Math.floor(a / b);
        const remainder = a % b;
        euclideanSteps.push({ step: stepNum, a, b, quotient, remainder });
        a = b;
        b = remainder;
        stepNum++;
      }
    }

    setResults({
      numbers,
      gcfValue,
      factorizations,
      commonFactors,
      euclideanSteps
    });
  };

  const handleClear = () => {
    setNumbersInput('');
    setResults(null);
    setError('');
  };

  const colors = {
    primary: '#2563eb',
    primaryHover: '#1d4ed8',
    secondary: '#7c3aed',
    accent: '#3b82f6',
    textPrimary: '#1e293b',
    textSecondary: '#475569',
    success: '#10b981',
    error: '#ef4444',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(37, 99, 235, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.7, margin: '0 auto 1rem', textAlign: 'left' as const },
    { fontSize: '1rem', maxWidth: '100%' },
    { fontSize: '1.125rem', maxWidth: '800px' },
    { fontSize: '1.125rem', maxWidth: '800px' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    {
      maxWidth: '100%',
      margin: '0 auto',
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden' as const,
    },
    {},
    { maxWidth: '700px' },
    { maxWidth: '800px' }
  );

  const toolHeaderStyle = {
    background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
    color: 'white',
    padding: '2rem',
    textAlign: 'center' as const,
  };

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const formStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const formGroupStyle = {
    marginBottom: '1.5rem',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 600,
    color: colors.textPrimary,
    fontSize: '0.95rem',
  };

  const textareaStyle = getResponsiveStyle(
    {
      width: '100%',
      border: `2px solid #e2e8f0`,
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      fontFamily: "'Courier New', monospace",
      resize: 'vertical' as const,
      minHeight: '100px',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' }
  );

  const inputHintStyle = {
    fontSize: '0.85rem',
    color: colors.textSecondary,
    marginTop: '0.5rem',
    display: 'block',
  };

  const buttonGroupStyle = getResponsiveStyle(
    { display: 'flex', gap: '1rem', marginTop: '1.5rem' },
    { flexDirection: 'column' as const },
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const clearButtonStyle = getResponsiveStyle(
    {
      flex: 1,
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: '#f1f5f9',
      color: '#475569',
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
      borderTop: '3px solid #e2e8f0',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: colors.textPrimary, display: 'flex', alignItems: 'center', gap: '0.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.5rem' }
  );

  const gcfResultStyle = {
    background: 'white',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    borderLeft: `4px solid ${colors.primary}`,
    marginBottom: '1.5rem',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
  };

  const gcfValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: colors.primary, margin: '0.5rem 0' },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '2.5rem' }
  );

  const gcfLabelStyle = {
    fontSize: '0.95rem',
    color: colors.textSecondary,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    fontWeight: 600,
  };

  const primeFactorsStyle = {
    background: 'white',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    marginBottom: '1.5rem',
  };

  const primeFactorsH4Style = {
    margin: '0 0 1rem 0',
    color: colors.textPrimary,
    fontSize: '1.1rem',
  };

  const factorRowStyle = {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '0.75rem',
    padding: '0.75rem',
    background: '#f8fafc',
    borderRadius: '0.5rem',
    flexWrap: 'wrap' as const,
  };

  const factorNumberStyle = {
    fontWeight: 700,
    color: colors.primary,
    minWidth: '80px',
  };

  const factorEqualsStyle = {
    margin: '0 0.75rem',
    color: colors.textSecondary,
  };

  const factorListStyle = {
    fontFamily: "'Courier New', monospace",
    color: '#334155',
  };

  const commonFactorStyle = {
    color: colors.success,
    fontWeight: 700,
  };

  const stepsStyle = {
    background: 'white',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    marginBottom: '1.5rem',
  };

  const stepItemStyle = {
    padding: '0.75rem',
    background: '#f8fafc',
    borderRadius: '0.5rem',
    marginBottom: '0.75rem',
    fontFamily: "'Courier New', monospace",
    fontSize: '0.9rem',
    color: '#334155',
  };

  const errorMessageStyle = {
    background: '#fef2f2',
    borderLeft: `4px solid ${colors.error}`,
    padding: '1rem',
    borderRadius: '0.5rem',
    color: '#991b1b',
    marginTop: '1rem',
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(37, 99, 235, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: 0 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem',
  };

  const listItemStyle = {
    marginBottom: '0.75rem',
  };

  const strongStyle = {
    color: colors.textPrimary,
    fontWeight: 600,
  };

  const linkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    borderBottom: `1px solid ${colors.primary}`,
    transition: 'all 0.3s ease',
  };

  const sectionImageStyle = getResponsiveStyle(
    {
      display: 'block',
      margin: '1.5rem auto',
      borderRadius: '0.75rem',
      maxWidth: '100%',
      height: 'auto' as const,
    },
    { width: '100%' },
    { width: '100%' },
    { width: '640px' }
  );

  const formulaStyle = {
    fontFamily: "'Courier New', monospace",
    background: '#f8fafc',
    padding: '0.875rem 1rem',
    borderRadius: '0.5rem',
    margin: '1rem 0',
    color: '#334155',
    fontSize: '1rem',
    textAlign: 'center' as const,
    borderLeft: `4px solid ${colors.primary}`,
  };

  const monospaceExampleStyle = {
    fontFamily: "'Courier New', monospace",
    background: '#f8fafc',
    padding: '0.875rem 1rem',
    borderRadius: '0.5rem',
    margin: '0.5rem 0',
    color: '#334155',
    fontSize: '0.95rem',
    whiteSpace: 'pre-line' as const,
    lineHeight: 1.6,
  };

  const tableWrapperStyle = {
    overflowX: 'auto' as const,
    marginBottom: '1.5rem',
    WebkitOverflowScrolling: 'touch' as const,
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.95rem',
  };

  const thStyle = {
    textAlign: 'left' as const,
    padding: '0.75rem',
    borderBottom: `2px solid ${colors.primary}`,
    background: '#eff6ff',
    color: colors.textPrimary,
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
  };

  const tdStyle = {
    padding: '0.75rem',
    borderBottom: '1px solid #e2e8f0',
    color: colors.textSecondary,
  };

  const tdMonospaceStyle = {
    ...tdStyle,
    fontFamily: "'Courier New', monospace",
  };

  const ctaCardStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(238, 242, 255, 0.9) 0%, rgba(224, 231, 255, 0.9) 100%)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(124, 58, 237, 0.12)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const ctaButtonLinkStyle = {
    ...calculateButtonStyle,
    display: 'inline-block' as const,
    textDecoration: 'none',
    marginTop: '0.5rem',
    background: 'linear-gradient(135deg, #ff4f6d 0%, #ff7a59 100%)',
    boxShadow: '0 12px 28px rgba(255, 79, 109, 0.28)',
  };

  const workedExamplesRows = [
    { numbers: t('workedExamplesRow1Numbers'), sharedFactors: t('workedExamplesRow1SharedFactors'), gcf: t('workedExamplesRow1Gcf') },
    { numbers: t('workedExamplesRow2Numbers'), sharedFactors: t('workedExamplesRow2SharedFactors'), gcf: t('workedExamplesRow2Gcf') },
    { numbers: t('workedExamplesRow3Numbers'), sharedFactors: t('workedExamplesRow3SharedFactors'), gcf: t('workedExamplesRow3Gcf') },
    { numbers: t('workedExamplesRow4Numbers'), sharedFactors: t('workedExamplesRow4SharedFactors'), gcf: t('workedExamplesRow4Gcf') },
    { numbers: t('workedExamplesRow5Numbers'), sharedFactors: t('workedExamplesRow5SharedFactors'), gcf: t('workedExamplesRow5Gcf') },
    { numbers: t('workedExamplesRow6Numbers'), sharedFactors: t('workedExamplesRow6SharedFactors'), gcf: t('workedExamplesRow6Gcf') },
  ];

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <img
          src="https://macaron-system.oss-ap-southeast-1.aliyuncs.com/image/web/pseo/edaab447.png"
          alt={t('gcf1ImageAlt')}
          loading="lazy"
          style={sectionImageStyle}
        />
        <p style={summaryTextStyle}>{t('subtitle')}</p>
        <p style={summaryTextStyle}>{t('subtitle2')}</p>

      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>🧮 {t('toolTitle')}</h2>
          </div>

          <form style={formStyle} onSubmit={handleCalculate}>
            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="numbers-input">
                {t('inputLabel')}
              </label>
              <textarea
                id="numbers-input"
                style={textareaStyle}
                placeholder={t('inputPlaceholder')}
                value={numbersInput}
                onChange={(e) => setNumbersInput(e.target.value)}
                required
              />
              <span style={inputHintStyle}>{t('inputHint')}</span>
            </div>

            <div style={buttonGroupStyle}>
              <button type="submit" style={calculateButtonStyle}>
                {t('calculateButton')}
              </button>
              <button type="button" style={clearButtonStyle} onClick={handleClear}>
                {t('clearButton')}
              </button>
            </div>
          </form>

          {error && (
            <div style={resultsStyle}>
              <div style={errorMessageStyle}>
                <strong>{t('errorTitle')}</strong> {error}
              </div>
            </div>
          )}

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>📊 {t('resultsTitle')}</h3>

              <div style={gcfResultStyle}>
                <div style={gcfLabelStyle}>{t('gcfLabel')}</div>
                <div style={gcfValueStyle}>{results.gcfValue}</div>
                <div style={{ color: colors.textSecondary, marginTop: '0.5rem' }}>
                  {t('gcfNotation', { numbers: results.numbers.join(', '), gcf: results.gcfValue })}
                </div>
              </div>

              <div style={primeFactorsStyle}>
                <h4 style={primeFactorsH4Style}>{t('primeFactorsTitle')}</h4>
                {results.numbers.map((num: number, index: number) => {
                  const factors = results.factorizations[index];
                  const commonFactorsCopy = [...results.commonFactors];

                  return (
                    <div key={index} style={factorRowStyle}>
                      <span style={factorNumberStyle}>{num}</span>
                      <span style={factorEqualsStyle}>=</span>
                      <span style={factorListStyle}>
                        {factors.map((f: number, i: number) => {
                          const isCommon = commonFactorsCopy.includes(f);
                          if (isCommon) {
                            const idx = commonFactorsCopy.indexOf(f);
                            commonFactorsCopy.splice(idx, 1);
                          }
                          return (
                            <span key={i}>
                              {i > 0 && ' × '}
                              <span style={isCommon ? commonFactorStyle : {}}>{f}</span>
                            </span>
                          );
                        })}
                      </span>
                    </div>
                  );
                })}
                <div style={{
                  ...factorRowStyle,
                  marginTop: '1rem',
                  borderTop: '2px solid #e2e8f0',
                  paddingTop: '1rem',
                }}>
                  <span style={factorNumberStyle}>{t('gcfLabel')}</span>
                  <span style={factorEqualsStyle}>=</span>
                  <span style={factorListStyle}>
                    <span style={commonFactorStyle}>
                      {primeFactorization(results.gcfValue).join(' × ')}
                    </span>
                    {' = '}
                    {results.gcfValue}
                  </span>
                </div>
              </div>

              {results.euclideanSteps.length > 0 && (
                <div style={stepsStyle}>
                  <h4 style={primeFactorsH4Style}>{t('euclideanTitle')}</h4>
                  {results.euclideanSteps.map((step: any, index: number) => (
                    <div key={index} style={stepItemStyle}>
                      {t('stepLabel', { step: step.step })}: {step.a} = {step.b} × {step.quotient} + {step.remainder}
                    </div>
                  ))}
                  <div style={{
                    ...stepItemStyle,
                    background: '#dbeafe',
                    color: '#1e40af',
                    fontWeight: 600,
                  }}>
                    {t('gcfLabel')} = {results.gcfValue}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <img
          src="https://macaron-system.oss-ap-southeast-1.aliyuncs.com/image/web/pseo/f6095a6a.png"
          alt={t('gcf2ImageAlt')}
          loading="lazy"
          style={sectionImageStyle}
        />
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('gcfVsGcdHcfTitle')}
        </h3>
        <p style={paragraphStyle}>{t('gcfVsGcdHcfAnswer')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <p style={paragraphStyle}>{t('howToUseDescription')}</p>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={listItemStyle}>
            <strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}
            <img
              src="https://macaron-system.oss-ap-southeast-1.aliyuncs.com/image/web/pseo/c6efda94.png"
              alt={t('gcf3ImageAlt')}
              loading="lazy"
              style={sectionImageStyle}
            />
          </li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight1Title')}</strong> {t('insight1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight2Title')}</strong> {t('insight2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight3Title')}</strong> {t('insight3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight4Title')}</strong> {t('insight4Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight5Title')}</strong> {t('insight5Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('methodsTitle')}</h2>
        <p style={paragraphStyle}>{t('methodsDescription')}</p>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('method1Title')}
        </h3>
        <p style={paragraphStyle}>{t('method1Description')}</p>
        <p style={paragraphStyle}>{t('method1Example')}</p>
        <p style={paragraphStyle}>{t('method1Note')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('method2Title')}
        </h3>
        <p style={paragraphStyle}>{t('method2Description')}</p>
        <p style={paragraphStyle}>{t('method2Example')}</p>
        <p style={paragraphStyle}>{t('method2Note')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('method3Title')}
        </h3>
        <p style={paragraphStyle}>{t('method3Description')}</p>
        <p style={paragraphStyle}>{t('method3Example')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('factorOutTitle')}</h2>
        <p style={paragraphStyle}>{t('factorOutParagraph1')}</p>
        <p style={monospaceExampleStyle}>{t('factorOutParagraph2')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('gcfLcmTitle')}</h2>
        <p style={paragraphStyle}>{t('gcfLcmParagraph1')}</p>
        <p style={paragraphStyle}>{t('gcfLcmForTwo')}</p>
        <p style={formulaStyle}>{t('gcfLcmFormula')}</p>
        <p style={monospaceExampleStyle}>{t('gcfLcmExample')}</p>
        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('gcfLcmRelationshipTitle')}
        </h3>
        <p style={paragraphStyle}>{t('gcfLcmRelationshipParagraph1')}</p>
        <p style={formulaStyle}>{t('gcfLcmRelationshipFormula')}</p>
        <p style={monospaceExampleStyle}>{t('gcfLcmRelationshipExample')}</p>
        <p style={paragraphStyle}>{t('gcfLcmRelationshipNote')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('simplifyFractionsTitle')}</h2>
        <p style={paragraphStyle}>{t('simplifyFractionsParagraph1')}</p>
        <p style={monospaceExampleStyle}>{t('simplifyFractionsExample')}</p>
        <p style={paragraphStyle}>{t('simplifyFractionsParagraph2')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('threeOrMoreTitle')}</h2>
        <p style={paragraphStyle}>{t('threeOrMoreParagraph1')}</p>
        <p style={paragraphStyle}>{t('threeOrMorePairwise')}</p>
        <p style={monospaceExampleStyle}>{t('threeOrMoreExample')}</p>
        <p style={paragraphStyle}>{t('threeOrMoreParagraph2')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('workedExamplesTitle')}</h2>
        <p style={paragraphStyle}>{t('workedExamplesIntro')}</p>
        <div style={tableWrapperStyle}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>{t('workedExamplesTableHeaderNumbers')}</th>
                <th style={thStyle}>{t('workedExamplesTableHeaderSharedFactors')}</th>
                <th style={thStyle}>{t('workedExamplesTableHeaderGcf')}</th>
              </tr>
            </thead>
            <tbody>
              {workedExamplesRows.map((row, index) => (
                <tr key={index}>
                  <td style={tdStyle}>{row.numbers}</td>
                  <td style={tdMonospaceStyle}>{row.sharedFactors}</td>
                  <td style={tdStyle}>{row.gcf}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={paragraphStyle}>{t('workedExamplesParagraphAfter')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.25rem' : '1.5rem', marginTop: '1.5rem' }}>
          {t('coprimeTitle')}
        </h3>
        <p style={paragraphStyle}>{t('coprimeParagraph1')}</p>
        <p style={monospaceExampleStyle}>{t('coprimeExample')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('applicationsTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('app1Title')}</strong> {t('app1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('app2Title')}</strong> {t('app2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('app3Title')}</strong> {t('app3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('app4Title')}</strong> {t('app4Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('app5Title')}</strong> {t('app5Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('faq1Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq1Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('faq2Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq2Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('faq3Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq3Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('faq4Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq4Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('faq5Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq5Answer')}</p>

        <h3 style={{ ...h2Style, fontSize: screenSize === 'mobile' ? '1.125rem' : '1.25rem', marginTop: '1.5rem' }}>
          {t('faq6Question')}
        </h3>
        <p style={paragraphStyle}>{t('faq6Answer')}</p>
      </section>

      <section style={ctaCardStyle}>
        <h2 style={h2Style}>{t('ctaTitle')}</h2>
        <p style={paragraphStyle}>{t('ctaParagraph1')}</p>
        <p style={paragraphStyle}>{t('ctaParagraph2')}</p>
        <a href="https://macaron.im/macaron-app" style={ctaButtonLinkStyle}>
          {t('ctaButtonText')}
        </a>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('relatedToolsTitle')}</h2>
        <ul style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://macaron.im/tools/math/number-sequence-calculator" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('relatedTools1Text')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://macaron.im/tools/health/body-type-calculator" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('relatedTools2Text')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://macaron.im/macaron-app" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('relatedTools3Text')}
            </a>
          </li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>
            <a href="https://byjus.com/maths/gcf/" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('ref1')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.geeksforgeeks.org/maths/greatest-common-factor-gcf/" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('ref2')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.calculator.net/gcf-calculator.html" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('ref3')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.mathsisfun.com/greatest-common-factor.html" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('ref4')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://en.wikipedia.org/wiki/Greatest_common_divisor" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('ref5')}
            </a>
          </li>
          <li style={listItemStyle}>
            <a href="https://www.khanacademy.org/math/cc-sixth-grade-math/cc-6th-expressions-and-variables/cc-6th-gcf/v/greatest-common-divisor" style={linkStyle} target="_blank" rel="noopener noreferrer">
              {t('ref6')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}
